// The single, canonical way any web surface (marketing + agent SPA) opens the
// user's Ori messaging thread — the "jump to lane" handoff.
//
// Every conversation with Ori happens in the messaging channel; web surfaces
// only hand off. The handoff is an `sms:` / `wa.me` deep link, but firing one
// is NOT reliable everywhere:
//   - Windows / Linux desktop  → no app registered for `sms:` → the click is a
//                                silent no-op.
//   - Android in-app webviews  → the scheme is frequently blocked / no-ops
//                                (see src/shared/user-agent/ua-classify.js).
//   - iOS / Android browsers   → open Messages / the SMS app.
//   - macOS                    → launches Messages.app via Continuity.
//
// So a bare `window.location.href = 'sms:…'` "works" on Apple devices and dies
// quietly on Windows + many Android contexts — which is exactly why those
// platforms felt unresponsive. This helper centralises the decision: on a
// platform that can't launch the app it does NOT navigate and instead invokes
// `onFallback` so the caller can reveal a copy-the-number affordance; on a
// platform that can, it fires the deep link and watches whether the app
// actually took over, falling back if it didn't.
//
// This mirrors the pattern the marketing MessageOriModal pioneered; both apps
// now share this one implementation.

import { getOs } from './ua-detect';

const HANDOFF_WATCH_MS = 2200;

/**
 * Why the handoff fell back — lets the fallback UI speak to the actual case:
 *   'desktop' — this platform (Windows/Linux) has no sms: handler at all.
 *   'no-app'  — the deep link fired but nothing took over (blocked webview, or
 *               no messaging app). We detect it by the tab staying foregrounded.
 */
export type LaneJumpFailReason = 'desktop' | 'no-app';

export interface LaneJumpHandlers {
  /** App launched — the tab backgrounded (visibilitychange / pagehide / blur). */
  onOpened?: () => void;
  /**
   * The deep link can't or didn't launch an app: desktop-other up front, or
   * still-foregrounded after the watch window. Reveal the number-to-copy UI.
   */
  onFallback?: (reason: LaneJumpFailReason) => void;
  /** Override the "did it open?" watch window (ms). */
  watchMs?: number;
}

/**
 * True when this platform can actually launch a messaging app from an `sms:` /
 * `wa.me` link. False on Windows / Linux desktop, where the scheme is a dead
 * end — callers should reveal the copy-the-number fallback instead of firing.
 */
export function canLaunchMessaging(): boolean {
  return getOs() !== 'other';
}

/**
 * Arm a "did the app actually open?" watcher WITHOUT navigating. For callers
 * that hand off via a native `<a href>` (the most reliable mobile gesture) and
 * only need to know whether it no-op'd. If the tab backgrounds the app opened
 * (`onOpened`); if we're still foregrounded after `watchMs` it failed
 * (`onFallback`). Returns a cleanup function — call it on unmount.
 */
export function watchOriLaneJump(handlers: LaneJumpHandlers = {}): () => void {
  if (typeof window === 'undefined') return () => {};
  let settled = false;
  let timer = 0;
  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') settle('opened');
  };
  const onAway = () => settle('opened');
  function cleanup() {
    if (timer) window.clearTimeout(timer);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onAway);
    window.removeEventListener('blur', onAway);
  }
  function settle(outcome: 'opened' | 'failed') {
    if (settled) return;
    settled = true;
    cleanup();
    if (outcome === 'opened') handlers.onOpened?.();
    else handlers.onFallback?.('no-app');
  }
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onAway);
  window.addEventListener('blur', onAway);
  timer = window.setTimeout(() => settle('failed'), handlers.watchMs ?? HANDOFF_WATCH_MS);
  return cleanup;
}

/**
 * Open the user's Ori thread via a pre-built `sms:` / `wa.me` deep link, from a
 * context that is NOT already a native `<a href>` (e.g. an onClick button).
 *
 * @param href  deep link from buildSmsHref() or a server-provided replyUrl.
 * @returns a cleanup function; call it on unmount to drop the watcher.
 *
 * Behaviour:
 *   - no href, or desktop-other (Windows/Linux) → `onFallback()`, no navigation.
 *   - otherwise → fire the deep link, then watch (see watchOriLaneJump).
 */
export function jumpToOriLane(href: string, handlers: LaneJumpHandlers = {}): () => void {
  const noop = () => {};
  const url = (href || '').trim();
  if (!url) {
    handlers.onFallback?.('no-app');
    return noop;
  }
  if (!canLaunchMessaging()) {
    handlers.onFallback?.('desktop');
    return noop;
  }
  // Arm the watcher BEFORE navigating so a fast app-switch isn't missed.
  const cleanup = watchOriLaneJump(handlers);
  window.location.href = url;
  return cleanup;
}
